import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const SORT_OPTS = [
  { v: "popularity", label: "Most Loved" },
  { v: "newest", label: "Newest" },
  { v: "price_asc", label: "Price: Low to High" },
  { v: "price_desc", label: "Price: High to Low" },
];

const AUDIENCES = [
  { v: "", label: "All" },
  { v: "women", label: "Women" },
  { v: "men", label: "Men" },
  { v: "children", label: "Children" },
];

export default function Shop() {
  const { category } = useParams();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [allCats, setAllCats] = useState([]);

  const q = params.get("q") || "";
  const sort = params.get("sort") || "popularity";
  const minPrice = params.get("min") || "";
  const maxPrice = params.get("max") || "";
  const audience = params.get("audience") || "";
  const subcategory = params.get("subcategory") || "";

  const title = useMemo(() => {
    if (q) return `Results for "${q}"`;
    const audPart = audience ? audience.charAt(0).toUpperCase() + audience.slice(1) : "";
    const catPart = category ? category.charAt(0).toUpperCase() + category.slice(1) : audPart ? "Jewelry" : "Shop All";
    return audPart ? `${audPart} · ${catPart}` : catPart;
  }, [category, q, audience]);

  useEffect(() => { api.get("/categories").then(({ data }) => setAllCats(data)); }, []);

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams({ sort, limit: "60" });
    if (category) qp.set("category", category);
    if (q) qp.set("q", q);
    if (minPrice) qp.set("min_price", minPrice);
    if (maxPrice) qp.set("max_price", maxPrice);
    if (audience) qp.set("audience", audience);
    if (subcategory) qp.set("subcategory", subcategory);
    api.get(`/products?${qp}`).then(({ data }) => {
      setItems(data.items || []); setTotal(data.total || 0);
    }).finally(() => setLoading(false));
  }, [category, q, sort, minPrice, maxPrice, audience, subcategory]);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k === "audience" || k === "category") next.delete("subcategory");
    setParams(next);
  };

  const subcats = useMemo(() => {
    if (!category) return [];
    return allCats.filter((c) => c.parent_slug === category);
  }, [category, allCats]);

  return (
    <main className="max-w-[1480px] mx-auto px-6 md:px-12 py-14 md:py-20" data-testid="shop-page">
      <Seo title={title} description={`Shop ${title} at Aura & Co. — hand-finished fine jewelry from our London atelier.`} />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
        <div>
          <p className="eyebrow text-neutral-500">Collection</p>
          <h1 className="font-serif text-4xl md:text-6xl mt-2 capitalize">{title}</h1>
          <p className="text-sm text-neutral-500 mt-2" data-testid="results-count">{total} pieces</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <select value={sort} onChange={(e) => set("sort", e.target.value)} data-testid="sort-select"
            className="border border-hair bg-white text-sm px-4 py-3 outline-none">
            {SORT_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          <input type="number" placeholder="Min £" value={minPrice} onChange={(e) => set("min", e.target.value)} data-testid="min-price"
            className="w-24 border border-hair bg-white text-sm px-3 py-3 outline-none" />
          <input type="number" placeholder="Max £" value={maxPrice} onChange={(e) => set("max", e.target.value)} data-testid="max-price"
            className="w-24 border border-hair bg-white text-sm px-3 py-3 outline-none" />
        </div>
      </div>

      {/* Audience chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="audience-chips">
        {AUDIENCES.map((a) => (
          <button key={a.v} onClick={() => set("audience", a.v)} data-testid={`chip-aud-${a.v || "all"}`}
            className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase border transition-colors ${
              audience === a.v ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-hair hover:border-neutral-900"
            }`}>{a.label}</button>
        ))}
      </div>

      {/* Subcategory chips when a category is active */}
      {category && subcats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-10" data-testid="sub-chips">
          <button onClick={() => set("subcategory", "")} data-testid="chip-sub-all"
            className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase border transition-colors ${
              !subcategory ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "bg-white text-neutral-700 border-hair hover:border-[var(--gold)]"
            }`}>All</button>
          {subcats.map((s) => (
            <button key={s.slug} onClick={() => set("subcategory", s.slug)} data-testid={`chip-sub-${s.slug}`}
              className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                subcategory === s.slug ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "bg-white text-neutral-700 border-hair hover:border-[var(--gold)]"
              }`}>{s.name}</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {Array(8).fill(0).map((_, i) => <div key={i} className="aspect-[4/5] bg-sand animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-neutral-500 py-20 text-center" data-testid="no-results">No pieces found. Try a different filter.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="product-grid">
          {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </main>
  );
}
